import { useCallback, useEffect, useMemo, useState } from "react";

import type * as QueryRepository from "@/components/features/settings/payment-credit/repository/query";

import { useRegistrationPayjp, useUpdatePaymentCredit } from "..";
import * as UseCase from "../../model/use-case";
import { SettingsPaymentCreditMainDialog } from "../_components/main/dialog";

type Props = {
	hotelID: string;
	serverModel: QueryRepository.ServerModel;
};

export const useSettingsPaymentCreditDialog = ({ hotelID, serverModel }: Props) => {
	const [open, setOpen] = useState(false);
	const handleOpenChange = useCallback((open: boolean) => {
		if (open) {
			setOpen(true);
		} else {
			setOpen(false);
		}
	}, []);
	const activateCreditCardSettingDialog = useCallback(() => {
		setOpen(true);
	}, []);
	const deactivateCreditCardSettingDialog = useCallback(() => {
		setOpen(false);
	}, []);

	const { queryData, viewModel, onSetViewModel } = UseCase.useInitialized({ hotelID, serverModel });

	const {
		onSetTargetElement,
		onChangeHasPayjpValidation,
		onCreatePayjpToken,
		onChangeCardHolderName,
		onChangeCardEmail,
	} = useRegistrationPayjp(viewModel, onSetViewModel);

	const [canEdit, setCanEdit] = useState(false);
	const onChangeCanEdit = useCallback((value: boolean) => {
		setCanEdit(value);
	}, []);

	const onSuccessCallbackForOnUpdate = useCallback(() => onChangeCanEdit, [onChangeCanEdit]);
	const _onUpdate = useUpdatePaymentCredit(hotelID, onCreatePayjpToken, onSuccessCallbackForOnUpdate);

	const onUpdate = useCallback(async () => {
		await _onUpdate().catch(() => {
			deactivateCreditCardSettingDialog();
		});
	}, [_onUpdate, deactivateCreditCardSettingDialog]);

	const isValid = !viewModel.hasPayjpValidation;

	useEffect(() => {
		onChangeCardEmail(queryData.hotelEmail);
	}, [queryData.hotelEmail, onChangeCardEmail]);

	const SettingsPaymentCreditDialog = useMemo(
		() => (
			<SettingsPaymentCreditMainDialog
				isValid={isValid}
				expired={queryData.expired}
				last4={queryData.last4}
				canEdit={canEdit}
				onChangeCanEdit={onChangeCanEdit}
				onSetTargetElement={onSetTargetElement}
				onChangeHasPayjpValidation={onChangeHasPayjpValidation}
				onChangeCardHolderName={onChangeCardHolderName}
				onUpdate={onUpdate}
				open={open}
				handleOpenChange={handleOpenChange}
			/>
		),
		[
			canEdit,
			handleOpenChange,
			isValid,
			onChangeCanEdit,
			onChangeHasPayjpValidation,
			onSetTargetElement,
			onUpdate,
			open,
			queryData.expired,
			queryData.last4,
			onChangeCardHolderName,
		],
	);

	return {
		activateCreditCardSettingDialog,
		deactivateCreditCardSettingDialog,
		SettingsPaymentCreditDialog,
	};
};
